import moment from 'moment/moment';
import 'moment/locale/he';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { Moment } from 'moment';
import CalendarDay from '../CalendarDay';
import { CalendarItems, DATE_FORMAT } from '../calendarWrapper/CalendarWrapper';
import { AllDayCalendarCell } from '../AllDayCalendarCell';
import { CurrentTimeDivider } from './components/CurrentTimeDivider';
import { useAuth } from '../../session/InternalAuthProvider';
import { getHoliday } from '../../common/getHoliday';
import { useLanguage } from '../../common/GeneralUtils';
import { AccountData, EnrichedEventData, OrderData, WaitingListStatus } from '../../api';
import { WaitingListIndicator } from './components/WaitingListIndicator';
import { ResourceHeaders } from '../calendar-items/ResourceHeaders';
import { calculateCapacityStats } from '../utils/capacityCalculationUtils';
import { HoursBordersHeader } from './components/HoursBordersHeader';
import { DEFAULT_START_OFFSET } from '../calendar-items/CalendarCalculator';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { useElementWidth } from '../utils/useElementWidth';
import {
  buildDaySummary,
  DaySummary,
  EMPTY_DAY_SUMMARY,
  peakSlotCount,
  toSummaryItems,
} from '../utils/calendarSummary';

const HOUR_HEIGHT_PX = 50;
const SLOT_SNAP_MINUTES = 15;

const HEADER_FORMAT_SMALL = 'ddd DD';
type MultipleDaysViewProps = {
  currentDate: string;
  calendarItems: CalendarItems;
  numOfDays: number;
  openOrder: (event: any, order: OrderData) => void;
  openEvent: (event: EnrichedEventData) => void;
};

const MultipleDaysView = ({ currentDate, calendarItems, numOfDays, openOrder, openEvent }: MultipleDaysViewProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const today = moment().startOf('day').format(DATE_FORMAT);
  const { isAtLeastSupervisor } = UseAuthPermissions();
  const { openSlotActionsMenu } = useCalendar();

  const hasSchedulerConfigs = (account?.shared_resources?.scheduler_configs?.length || 0) > 0;
  const shouldShowResources = hasSchedulerConfigs && numOfDays === 1;

  const capacityStats = useMemo(
    () => calculateCapacityStats(calendarItems, account, currentDate, numOfDays),
    [calendarItems, account, currentDate, numOfDays],
  );

  const summarizeBusyDays = !!account?.calendar_display_settings?.summarize_busy_days;
  const dayColumnRef = useRef<HTMLDivElement>(null);
  const dayColumnWidth = useElementWidth(dayColumnRef);

  const daySummaries = useMemo(() => {
    const summaries: Record<string, DaySummary> = {};
    [...Array(numOfDays).keys()].forEach((numOfDay) => {
      const date = moment(currentDate).add(numOfDay, 'days').format(DATE_FORMAT);
      summaries[date] =
        shouldShowResources || !summarizeBusyDays
          ? EMPTY_DAY_SUMMARY
          : buildDaySummary(
              toSummaryItems(calendarItems.orders[date] || [], calendarItems.events[date] || []),
              dayColumnWidth,
            );
    });
    return summaries;
  }, [calendarItems, currentDate, numOfDays, dayColumnWidth, shouldShowResources, summarizeBusyDays]);
  const peak = useMemo(() => peakSlotCount(Object.values(daySummaries)), [daySummaries]);

  useEffect(() => {
    const lastScrollPosition = localStorage.getItem('webook_lastCalendarScroll');
    // @ts-ignore
    document.getElementById('calendar-container').scrollTo({ top: lastScrollPosition });
  }, []);
  const handleScroll = (event: any) => {
    localStorage.setItem('webook_lastCalendarScroll', event.currentTarget.scrollTop);
  };

  // Only clicks on the grid itself (an hour cell or an empty table column) count: items on it have their own click.
  const onDayColumnClick = (event: React.MouseEvent<HTMLElement>, date: string) => {
    if (!isAtLeastSupervisor || !(event.target as HTMLElement).closest('[data-calendar-slot]')) {
      return;
    }
    const offsetPx = event.clientY - event.currentTarget.getBoundingClientRect().top;
    const minutes = Math.floor(((offsetPx / HOUR_HEIGHT_PX) * 60) / SLOT_SNAP_MINUTES) * SLOT_SNAP_MINUTES;
    openSlotActionsMenu({
      slot: { date, startSeconds: DEFAULT_START_OFFSET * 3600 + minutes * 60 },
      anchorPosition: { top: event.clientY, left: event.clientX },
    });
  };

  const isNumOfDayToday = (numOfDay: number) => moment(currentDate).add(numOfDay, 'days').format(DATE_FORMAT) === today;
  const cellBackgroundColor = (date: Moment) =>
    date.format(DATE_FORMAT) === today ? theme.palette.mb.todayWash : theme.palette.mb.surface;

  return (
    <div className="calendar-container" id="calendar-container" onScroll={handleScroll}>
      <Box className="sticky-part">
        <div className="shadow-box" />
        {numOfDays !== 1 && (
          <div className="flex header-row">
            <Box
              className="cell first-column"
              sx={{
                borderBottom: 'none',
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
              }}>
              <Box
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'mb.lineSoft',
                  width: '12px',
                  height: '50px',
                  boxSizing: 'border-box',
                }}
              />
            </Box>
            {[...Array(numOfDays).keys()].map((numOfDay) => {
              const date = moment(currentDate).add(numOfDay, 'days');
              const dateCloned = date.clone();
              const isTodayHeader = dateCloned.format(DATE_FORMAT) === today;
              const waitingListItemsNumber = calendarItems.waitingList[dateCloned.format(DATE_FORMAT)]?.filter(
                (x) => x.status === WaitingListStatus.ACTIVE,
              ).length;
              return (
                <Grid
                  minWidth="24px"
                  container
                  flexDirection="column"
                  key={`header-${numOfDay}`}
                  sx={{
                    backgroundColor: isTodayHeader ? 'primary.light' : cellBackgroundColor(date),
                    color: isTodayHeader ? 'primary.main' : undefined,
                    fontWeight: isTodayHeader ? 600 : undefined,
                    minHeight: 64,
                    gap: '2px',
                    paddingBlock: 1,
                  }}
                  className={`cell flex-1`}
                  justifyContent="center">
                  <Grid container justifyContent="center">
                    <Typography fontSize={17} fontWeight={600}>
                      {dateCloned.clone().locale(language).format(HEADER_FORMAT_SMALL)}
                    </Typography>
                  </Grid>
                  <Typography fontSize="12px" color="text.secondary">
                    {getHoliday(account.holiday_calendar, dateCloned.format(DATE_FORMAT), language)}
                  </Typography>
                  <WaitingListIndicator date={dateCloned.format(DATE_FORMAT)} amountOfItems={waitingListItemsNumber} />
                  {(numOfDays === 3 || numOfDays === 7) && !isSmallScreen && (
                    <Box>
                      {(() => {
                        const dayStats = capacityStats.daily.find((day) => day.date === dateCloned.format(DATE_FORMAT));
                        if (dayStats && (dayStats.orderCount > 0 || dayStats.personCount > 0)) {
                          return (
                            <Typography variant="caption" fontSize="12px" color="text.secondary">
                              {dayStats.orderCount > 0 && `${dayStats.orderCount} ${t('capacity.orders')}`}
                              {dayStats.orderCount > 0 && dayStats.personCount > 0 && ' • '}
                              {dayStats.personCount > 0 && `${dayStats.personCount} ${t('capacity.persons')}`}
                            </Typography>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  )}
                </Grid>
              );
            })}
          </div>
        )}
        {numOfDays === 1 && (
          <div className="flex header-row">
            <Box
              className="cell first-column"
              sx={{
                borderBottom: 'none',
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
              }}>
              <Box
                className="first-column"
                sx={{
                  borderBottom: 'none',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-end',
                }}>
                {numOfDays === 1 && (
                  <Grid container flexDirection="column" alignItems="center">
                    <Grid item>
                      <Typography>{moment(currentDate).locale(language).format('ddd')}</Typography>
                    </Grid>
                    <Grid item>
                      <Typography>{moment(currentDate).format('DD')}</Typography>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Box>
            <Grid
              minWidth="24px"
              pt={1}
              container
              flexDirection={'column'}
              justifyContent="flex-end"
              alignItems="center">
              <Typography fontSize="12px" color="text.secondary">
                {getHoliday(account.holiday_calendar, moment(currentDate).format(DATE_FORMAT), language)}
              </Typography>
              <WaitingListIndicator
                date={currentDate}
                amountOfItems={
                  calendarItems.waitingList[currentDate]?.filter((x) => x.status === WaitingListStatus.ACTIVE).length
                }
              />
              {shouldShowResources && <ResourceHeaders />}
            </Grid>
          </div>
        )}
        <div className="flex body-row">
          <Box className=" first-column" />
          {[...Array(numOfDays).keys()].map((numOfDay) => {
            return (
              <div
                key={numOfDay}
                style={{ backgroundColor: cellBackgroundColor(moment(currentDate).add(numOfDay, 'days')) }}
                className={` flex-1`}>
                <AllDayCalendarCell
                  unavailabilities={
                    calendarItems.unavailabilities[moment(currentDate).add(numOfDay, 'days').format(DATE_FORMAT)]
                  }
                  notes={calendarItems.notes[moment(currentDate).add(numOfDay, 'days').format(DATE_FORMAT)]}
                />
              </div>
            );
          })}
        </div>
      </Box>

      <Box style={{ position: 'relative' }}>
        <div className="flex body-row last-body-row">
          <div className="cell first-column cell">
            <HoursBordersHeader account={account} />
          </div>
          {[...Array(numOfDays).keys()].map((numOfDay) => {
            const date = moment(currentDate).add(numOfDay, 'days').format(DATE_FORMAT);
            return (
              <Grid
                key={numOfDay}
                ref={numOfDay === 0 ? dayColumnRef : undefined}
                className={`cell flex-1`}
                onClick={(event) => onDayColumnClick(event, date)}
                sx={{
                  backgroundColor: cellBackgroundColor(moment(currentDate).add(numOfDay, 'days')),
                  borderInlineStart: `1px solid ${theme.palette.mb.lineSoft}`,
                  ...(isAtLeastSupervisor && {
                    '& [data-calendar-slot="hour"]': { cursor: 'pointer' },
                    '& [data-calendar-slot="hour"]:hover': { backgroundColor: 'action.hover' },
                  }),
                }}>
                {isNumOfDayToday(numOfDay) && <CurrentTimeDivider />}
                <CalendarDay
                  dateToRender={date}
                  items={calendarItems}
                  openOrder={openOrder}
                  openEvent={openEvent}
                  numOfDays={numOfDays}
                  summary={daySummaries[date]}
                  peakSlotCount={peak}
                />
              </Grid>
            );
          })}
        </div>
      </Box>
    </div>
  );
};
export default MultipleDaysView;
